/**
 * AdeHouse — simple, direct add-to-cart for the custom product-grid cards.
 *
 * Deliberately bypasses the theme's ProductFormComponent/on:submit machinery
 * (variant pickers, quantity selectors, section-rendering refs) — that stack
 * has too many moving parts a stripped-down card doesn't provide, and a
 * missing piece fails silently. This talks to Shopify's documented
 * /cart/add.js endpoint directly, then /cart.js for the fresh totals, and
 * flips [data-added] on the clicked button for the CSS confirmation state.
 *
 * Loaded via <script type="module" src="{{ 'ade-add-to-cart.js' | asset_url }}">.
 * ES modules execute once per resolved URL no matter how many times the tag
 * appears on the page, so this is safe to include from every section that
 * needs it without double-binding the click listener.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const CONFIRM_DURATION_MS = 1600;
let sourceIdCounter = 0;

/**
 * Resolves the variant id to add. Cards render a fixed id in
 * data-ade-add-to-cart. Pages with a variant picker (e.g. the main product
 * page) instead point data-ade-variant-input at the id of a hidden input
 * that other on-page JS keeps in sync as the customer changes options —
 * reading it live means we always add whatever is currently selected.
 * @param {HTMLButtonElement} button
 */
function resolveVariantId(button) {
  const inputId = button.dataset.adeVariantInput;
  if (inputId) {
    const input = document.getElementById(inputId);
    if (input instanceof HTMLInputElement && input.value) return input.value;
  }
  return button.dataset.adeAddToCart || null;
}

/** @param {HTMLButtonElement} button */
function resolveQuantity(button) {
  const inputId = button.dataset.adeQuantityInput;
  if (inputId) {
    const input = document.getElementById(inputId);
    const value = input instanceof HTMLInputElement ? Number(input.value) : NaN;
    if (Number.isFinite(value) && value > 0) return value;
  }
  return 1;
}

/** @param {MouseEvent} event */
function onClick(event) {
  const target = /** @type {HTMLElement} */ (event.target);
  const button = target.closest('[data-ade-add-to-cart]');
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  event.preventDefault();

  const variantId = resolveVariantId(button);
  if (!variantId) return;
  const quantity = resolveQuantity(button);

  button.disabled = true;

  // Mirrors the fields the native ProductFormComponent puts on its resolved
  // CartLinesUpdateEvent detail (source/sourceId/productId/itemCount) — the
  // Shopify Web Pixels that power Google & YouTube's Add to Cart / Begin
  // Checkout / Purchase conversion tracking key off this shape. Without it
  // the cart still updates for the shopper, but the pixel has nothing to
  // read and the whole downstream funnel (Google Ads conversions) goes dark.
  const sourceId = button.id || `ade-add-to-cart-${++sourceIdCounter}`;
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity }],
      promise: deferred.promise,
    })
  );

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: Number(variantId), quantity }] }),
  })
    .then((response) => response.json())
    .then((result) => {
      if (result.status) {
        throw new Error(result.description || result.message || 'Add to cart failed');
      }
      const addedItem = Array.isArray(result.items) ? result.items[0] : result;
      return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((response) =>
        response.json().then((cart) => ({ cart, addedItem }))
      );
    })
    .then(({ cart, addedItem }) => {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'ade-add-to-cart',
          sourceId,
          productId: addedItem?.product_id != null ? String(addedItem.product_id) : undefined,
          itemCount: quantity,
          didError: false,
        },
      });

      button.disabled = false;
      button.dataset.added = 'true';
      setTimeout(() => {
        delete button.dataset.added;
      }, CONFIRM_DURATION_MS);
    })
    .catch((error) => {
      console.error('[ade-add-to-cart]', error);
      deferred.reject(error);
      button.disabled = false;

      document.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Add to cart failed',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    });
}

document.addEventListener('click', onClick);
